import React, { useCallback } from 'react';
import UrlMultiInput from '../shared/UrlMultiInput';

const Step1_UrlInput = ({
  mode = 'text',
  urls,
  setUrls,
  onNext,
  loading,
  error,
  imageFiles = [],
  setImageFiles,
}) => {
  const handleRemoveUrl = useCallback((indexToRemove) => {
    setUrls(prev => prev.filter((_, index) => index !== indexToRemove));
  }, [setUrls]);

  const clearAllUrls = useCallback(() => {
    setUrls([]);
  }, [setUrls]);

  const displayLabel = useCallback((url) => {
    return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  }, []);

  const handleFileSelect = (event) => {
    if (!setImageFiles) return;
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const filtered = files.filter((file) => /\.(jpg|jpeg|png)$/i.test(file.name));
    if (filtered.length === 0) {
      alert('Only JPG and PNG files are supported.');
      return;
    }

    setImageFiles((prev) => {
      const existingNames = new Set(prev.map((file) => file.name));
      const deduped = filtered.filter((file) => !existingNames.has(file.name));
      return [...prev, ...deduped];
    });
    event.target.value = '';
  };

  const handleRemoveFile = (name) => {
    if (!setImageFiles) return;
    setImageFiles((prev) => prev.filter((file) => file.name !== name));
  };

  const validUrlsCount = urls.length;
  const validFileCount = imageFiles.length;
  const hasPricingSources = validUrlsCount > 0 || validFileCount > 0;
  const isPricingMode = mode === 'pricing';

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Step 1: {isPricingMode ? 'Add Pricing List Sources' : 'Add Clinic Page URLs'}
        </h2>
        <p className="text-gray-600">
          {isPricingMode
            ? 'Provide pricing list image URLs or upload image files to extract pricing Q&A.'
            : 'Enter the URLs of clinic pages you want to scrape for QA knowledge base'}
        </p>
        {isPricingMode && (
          <p className="text-sm text-blue-600 mt-2">
            Pricing list mode accepts image URLs (ending with .jpg / .png) or direct image uploads.
          </p>
        )}
      </div>

      {/* URL Input Section */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <div className="mb-4">
          <UrlMultiInput
            urls={urls}
            setUrls={setUrls}
            rows={4}
            placeholder="example1.com, example2.com (press Enter after each URL)"
            label="Paste or type clinic/pricing URLs:"
            description="Hint: example1.com, example2.com (press Enter after each URL)."
          />
        </div>

        {validUrlsCount > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>{validUrlsCount} URL{validUrlsCount !== 1 ? 's' : ''} ready to process</span>
              <button
                type="button"
                onClick={clearAllUrls}
                className="text-xs text-red-600 hover:text-red-700 font-medium"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {urls.map((url, index) => (
                <span
                  key={`${url}-${index}`}
                  className="inline-flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded-full shadow-sm"
                >
                  <span className="max-w-xs truncate mr-2">{displayLabel(url)}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveUrl(index)}
                    className="text-gray-500 hover:text-red-600"
                    aria-label={`Remove ${url}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {isPricingMode && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Or upload pricing list images (JPG / PNG):
            </label>
            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={handleFileSelect}
              className="w-full text-sm text-gray-600"
            />
            {validFileCount > 0 && (
              <div className="mt-3 space-y-2">
                <div className="text-sm text-gray-600">
                  {validFileCount} image{validFileCount !== 1 ? 's' : ''} selected
                </div>
                <ul className="space-y-1">
                  {imageFiles.map((file) => (
                    <li
                      key={file.name}
                      className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2 text-sm"
                    >
                      <span className="truncate pr-2">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(file.name)}
                        className="text-red-600 hover:text-red-700 text-xs font-medium"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Next Button */}
      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={(isPricingMode ? !hasPricingSources : validUrlsCount === 0) || loading}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {loading ? 'Loading...' : 'Next Step →'}
        </button>
      </div>
    </div>
  );
};

export default Step1_UrlInput;

